import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePublishedGoogleReviews, useGoogleReviewSettings, type GoogleReview } from '@/hooks/useGoogleReviews';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { interleaveByGender } from '@/utils/reviewGender';


function GoogleLogo({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

function Stars({ rating, size = 'md', className = '' }: { rating: number; size?: 'sm' | 'md' | 'xs'; className?: string }) {
  const cls = size === 'xs' ? 'w-3.5 h-3.5' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className={`flex gap-0.5 ${className}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cls}
          style={{
            color: i <= Math.round(rating) ? '#fbbc04' : '#e5e7eb',
            fill: i <= Math.round(rating) ? '#fbbc04' : '#e5e7eb',
          }}
        />
      ))}
    </div>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' });
  } catch {
    return '';
  }
}

const TRUNCATE_LENGTH = 220;

function stripTranslation(t: string | null | undefined): string {
  if (!t) return '';
  return t.replace(/\s*\(Translated by Google\)[\s\S]*$/i, '').trimEnd();
}

function ReviewCard({ review }: { review: GoogleReview }) {
  const [expanded, setExpanded] = useState(false);
  const cleanText = stripTranslation(review.text);
  const isLong = cleanText.length > TRUNCATE_LENGTH;
  const displayText = !isLong || expanded
    ? cleanText
    : cleanText.slice(0, TRUNCATE_LENGTH).trimEnd() + '…';

  return (
    <div className="snap-center md:snap-start shrink-0 w-[240px] md:w-[248px] lg:w-[260px] bg-muted/60 h-full flex flex-col">
      <div className="flex flex-col items-start text-left px-5 py-5 md:px-6 md:py-6 h-full justify-between gap-4">
        <div>
          <p className="text-sm md:text-base leading-relaxed text-gray-800 whitespace-pre-wrap">
            {displayText}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-sm text-primary hover:underline"
            >
              {expanded ? 'Visa mindre' : 'Läs mer'}
            </button>
          )}
          <div className="mt-3">
            <Stars rating={review.rating} size="xs" />
          </div>
        </div>
        <div>
          <p className="font-semibold text-sm md:text-base text-gray-900">
            {review.author_name}
          </p>
          <p className="text-xs md:text-sm text-gray-500 mt-0.5">{formatDate(review.review_date)}</p>
        </div>
      </div>
    </div>
  );
}

interface Props {
  title?: string;
  className?: string;
}

export default function GoogleReviewsSingle({ title = 'Vad våra besökare säger', className = '' }: Props) {
  const { data: reviews, isLoading } = usePublishedGoogleReviews();
  const { data: settings } = useGoogleReviewSettings();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAdjustingRef = useRef(false);

  const { average, count } = useMemo(() => {
    const totalAvg = settings?.google_rating_average;
    const totalCnt = settings?.google_rating_count;
    if (totalAvg != null && totalCnt != null) {
      return { average: totalAvg, count: totalCnt };
    }
    if (!reviews || !reviews.length) return { average: 0, count: 0 };
    const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
    return { average: sum / reviews.length, count: reviews.length };
  }, [reviews, settings]);

  // Varva kvinnliga/manliga recensenter, sedan duplicera 3x för endless scroll
  const loopedReviews = useMemo(() => {
    if (!reviews || !reviews.length) return [];
    const ordered = interleaveByGender(reviews);
    return [...ordered, ...ordered, ...ordered];
  }, [reviews]);


  // Start scroll in the middle copy
  useLayoutEffect(() => {
    if (!scrollRef.current || !reviews || !reviews.length) return;
    const el = scrollRef.current;
    const singleWidth = el.scrollWidth / 3;
    el.scrollLeft = singleWidth;
  }, [reviews]);

  // Loop detection
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !reviews || !reviews.length) return;
    const onScroll = () => {
      if (isAdjustingRef.current) return;
      const singleWidth = el.scrollWidth / 3;
      if (el.scrollLeft <= singleWidth * 0.25) {
        isAdjustingRef.current = true;
        el.scrollLeft += singleWidth;
        requestAnimationFrame(() => {
          isAdjustingRef.current = false;
        });
      } else if (el.scrollLeft >= singleWidth * 1.75) {
        isAdjustingRef.current = true;
        el.scrollLeft -= singleWidth;
        requestAnimationFrame(() => {
          isAdjustingRef.current = false;
        });
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [reviews]);

  if (isLoading) return null;
  if (!reviews || !reviews.length) return null;

  const profileUrl = settings?.google_profile_url || '';
  const scrollBy = (direction: number) => {
    if (!scrollRef.current) return;
    const cardWidth = 260;
    scrollRef.current.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  };

  return (
    <section className={`py-8 md:py-12 ${className}`}>
      {/* Rubrik, betyg och verifieringsbadge ovanför korten, centrerat */}
      <div className="flex flex-col items-center mb-6 md:mb-8 gap-3">
        <h1 className="text-center">{title}</h1>
        <div className="flex items-center gap-2">
          <Stars rating={average} size="sm" />
          <span className="text-sm font-medium text-gray-900">
            {average.toFixed(2).replace('.', ',')}
          </span>
          <span className="text-sm text-gray-500">({count})</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <GoogleLogo className="w-4 h-4" />
          <span className="font-medium">via Google</span>
        </div>
      </div>

      {/* Horisontellt scrollande kort med fade på sidorna */}
      <div className="relative w-full">
        {/* Fade-overlays */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 z-10"
          style={{ background: 'linear-gradient(to right, #ffffff, rgba(255,255,255,0))' }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 z-10"
          style={{ background: 'linear-gradient(to left, #ffffff, rgba(255,255,255,0))' }}
          aria-hidden="true"
        />
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x pb-4 pl-[calc(50vw_-_120px)] pr-[calc(50vw_-_120px)] md:px-0 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loopedReviews.map((review, idx) => (
            <ReviewCard key={`${review.id}-${idx}`} review={review} />
          ))}
        </div>
      </div>

      {/* Pilar under korten */}
      <div className="flex justify-center items-center gap-4 mt-6">
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Föregående recension"
          className="w-9 h-9 rounded-full border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Nästa recension"
          className="w-9 h-9 rounded-full border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {profileUrl && (
        <div className="text-center mt-4">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gray-600 hover:text-gray-900 underline-offset-4 hover:underline"
          >
            Se alla recensioner på Google →
          </a>
        </div>
      )}
    </section>
  );
}
